import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { ToastrService } from 'ngx-toastr';

import {
	SymbologysService,
	LBSymbology,
	LBSymbol,
	LBSymbologyType,
	LBCompatibleSymbology
} from './../../../../common-services/symbologys.service';
import { LayersService, GeneralLayer, LayerColumn } from '../../../../common-services/layers.service';
import { LbeSettingService } from './../../../../common-services/lbe-setting.service';

// Extended layer column
interface LayerColumnE extends LayerColumn {
	isIntervalable?: boolean;
}

export interface SymbologyModalData {
	symbology?: LBSymbology;
	columns: LayerColumnE[];
	layers?: GeneralLayer[];
}
export interface SymbologyValueType {
	name: string; // For display use
	value: LBSymbologyType;
	checkDisable: Function;
}
export interface LbeSymbologyModalOutput {
	symbology: LBSymbology;
	symbolCol: LayerColumnE;
}

@Component({
	selector: 'app-lbe-symbology-modal',
	templateUrl: './lbe-symbology-modal.component.html',
	styleUrls: ['./lbe-symbology-modal.component.css']
})
export class LbeSymbologyModalComponent implements OnInit {

	// Changed symbology
	public symbology: LBSymbology;
	// Attribute Columns in the selected layer
	public columns: LayerColumnE[] = [];
	public selectedSymbCol: LayerColumnE;
	public selectedTipCol: LayerColumnE;
	public selectedLabCol: LayerColumnE;

	public compatibleSymbology: LBCompatibleSymbology;
	public compatibles: LBCompatibleSymbology[] = [];

	// Properties under add action
	public propsAdd: {
		layers: GeneralLayer[]; // Layers for selection
		selectedLayer: GeneralLayer;
	} = {
		layers: null,
		selectedLayer: null
	};

	// // Original symbology passed from data
	// private originalSymb: LBSymbology;

	//
	public valueType: SymbologyValueType;
	public valueTypes: SymbologyValueType[];

	public action: 'edit' | 'add'; // Edit a symbology or add a symbology

	// Nominal/Interval/Ordinal properties including symbols
	public symbolNom: { symbols: LBSymbol[] } = {
		symbols: []
	};
	public symbolInt: { symbols: LBSymbol[] } = {
		symbols: []
	};
	public symbolOrd: { symbols: LBSymbol[] } = {
		symbols: []
	};
	// NOMINAL symbology's distinct values list from database
	public nominalValuesDB: any[] = [];
	public intervalPrecision = 1; // 1, 0.1, 0.01...
	// INTERVAL symbology's min/max value from database (vs. user-defined min/max)
	public intervalMinMaxDB: number[] = [];
	// Interval legend group number
	public intLegendGroupNum = 0;
	constructor(
		@Inject(MAT_DIALOG_DATA) public data: SymbologyModalData,
		public dialogRef: MatDialogRef<LbeSymbologyModalComponent>,
		private toastr: ToastrService,
		private layersService: LayersService,
		private lbeSettingService: LbeSettingService,
		private symbologysService: SymbologysService
	) { }

	async selectLayer() {
		if (!this.propsAdd.selectedLayer) { return; }
		// Update symbology layerId
		this.symbology.layerId = this.propsAdd.selectedLayer.id;
		this.columns = await this.layersService.getLayerColumns(this.propsAdd.selectedLayer.id, 'attr');
		// Clear selected column
		this.selectedSymbCol = null;
		this.selectedTipCol = null;
		this.selectedLabCol = null;
		// Get compatible symbologys
		this.compatibles = await this.symbologysService.getSymbologyCompatible(this.symbology.layerId);
	}
	async changeSymbCol() {
		// Check tooltip & label columns
		// If they don't exist, OR they are the same column as symbology before changing, change them to the same as symbology column
		if (!this.selectedTipCol || this.symbology.layerColumnId === this.selectedTipCol.columnId) {
			this.selectedTipCol = this.selectedSymbCol;
			this.changeTipCol();
		}
		if (!this.selectedLabCol || this.symbology.layerColumnId === this.selectedLabCol.columnId) {
			this.selectedLabCol = this.selectedSymbCol;
			this.changeLabCol();
		}
		//
		// Update symbology symbol column
		this.symbology.layerColumnId = this.selectedSymbCol.columnId;
		this.symbology.spec.symCol = this.selectedSymbCol.name;
		await this.updateSymbologyValues();
	}
	changeTipCol() {
		// Update symbology tooltip column
		this.symbology.spec.tipCol = this.selectedTipCol.name;
	}
	changeLabCol() {
		this.symbology.spec.labCol = this.selectedLabCol.name;
	}
	async changeSymbolType() {
		this.symbology.spec.symType = this.valueType.value;
		await this.updateSymbologyValues();
	}
	private async updateSymbologyValues() {
		switch (this.symbology.spec.symType) {
			case 'INTERVAL':
				// Update intervavlMinMaxDB using database values
				if (this.selectedSymbCol && this.symbology.layerId) {
					try {
						this.intervalMinMaxDB = await this.symbologysService.getSymbologyValues(
							this.symbology.layerId,
							this.selectedSymbCol.columnId,
							'INTERVAL'
						);
						this.selectedSymbCol.isIntervalable = true;
					} catch (err) {
						this.selectedSymbCol.isIntervalable = false;
						this.toastr.error(
							'Column ' +
							this.selectedSymbCol.name +
							' can not be used to create an Interval Symbology. Please Switch to Nominal Symbology.'
						);
					}
				} // TODO: handle case: user already define min/max, should database min/max override it?
				else {
					this.intervalMinMaxDB = [null, null];
				}
				// Update symbology's min/max
				if (!this.symbology.spec.minVal && this.symbology.spec.minVal !== 0) {
					this.symbology.spec.minVal = this.intervalMinMaxDB[0];
				}
				if (!this.symbology.spec.maxVal && this.symbology.spec.maxVal !== 0) {
					this.symbology.spec.maxVal = this.intervalMinMaxDB[1];
				}
				this.intLegendGroupNum = this.symbolInt.symbols.length;
				break;
			case 'NOMINAL':
				if (this.selectedSymbCol && this.symbology.layerId) {
					this.nominalValuesDB = await this.symbologysService.getSymbologyValues(
						this.symbology.layerId,
						this.selectedSymbCol.columnId,
						'NOMINAL'
						// TODO: { pageSize: 20}
					);
				} else {
					this.nominalValuesDB = [];
				}
		}
	}
	onColorChange(e, symbol: LBSymbol) {
		symbol.color = e;
	}
	onStyleChange(e, symbol: LBSymbol) {
		symbol.size = e.size;
		symbol.style = e.shape;
	}

	selectCompatibles(comp: LBCompatibleSymbology) {
		this.compatibleSymbology = comp;
		const cSpec = this.compatibleSymbology.spec;
		this.symbology.spec = {
			name: cSpec.name,
			symCol: cSpec.symCol,
			labCol: cSpec.labCol,
			tipCol: cSpec.tipCol,
			symType: cSpec.symType,
			symbols: JSON.parse(JSON.stringify(cSpec.symbols))
		};
		this.selectedSymbCol = this.columns.find((c) => c.name === this.symbology.spec.symCol);
		this.selectedLabCol = this.columns.find((c) => c.name === this.symbology.spec.labCol);
		this.selectedTipCol = this.columns.find((c) => c.name === this.symbology.spec.tipCol);
		this.valueType = this.valueTypes.find((t) => t.value === this.symbology.spec.symType);
		switch (this.symbology.spec.symType) {
			case 'INTERVAL':
				this.symbolInt.symbols = this.symbology.spec.symbols;
				break;
			case 'NOMINAL':
				this.symbolNom.symbols = this.symbology.spec.symbols;
				break;
			case 'ORDINAL':
				this.symbolOrd.symbols = this.symbology.spec.symbols;
		}
		this.changeSymbolType();
	}

// Nominal Functions
	removeSymbolNom(idx) {
		this.symbolNom.symbols.splice(idx, 1);
	}
	addSymbolNom() {
		this.symbolNom.symbols.push(this.symbologysService.getEmptySymbol());
	}
	private updateNominalGroupWithDbValues() {
		// TODO!!!
		// this.symbolNom.symbols = this.nominalValuesDB.map((d) => {
		// 	return {
		// 		color: this.pa,
		// 		lab: d,
		// 		size:,
		// 		style: ,
		// 		val: d
		// 	};
		// });
	}
// Interval Functions
	removeSymbolInt(idx) {
		this.symbolInt.symbols.splice(idx, 1);
	}
	// Add a symbol after the provided index
	addSymbolInt(idx) {
		const symbol = this.symbologysService.getEmptySymbol();
		symbol.val = [];
		const prev = this.symbolInt.symbols[idx]; // Previous symbol
		const next = this.symbolInt.symbols[idx + 1]; // Next symbol
		if (prev) {
			symbol.val[0] = prev.val[1] + this.intervalPrecision;
		}
		if (next) {
			symbol.val[1] = next.val[0] - this.intervalPrecision;
		}
		this.adjustSymbolValueInt(symbol.val);
		// Splice would mess up ngfor (?) without trackByFn, causing the next symbol displayed the same as newly added symbol
		this.symbolInt.symbols.splice(idx + 1, 0, symbol);
		// Update legend group num
		this.intLegendGroupNum = this.symbolInt.symbols.length;
	}
	adjustSymbolValueInt(val: number[]) {
		if (val[0] >= val[1]) {
			val[1] = val[0];
		}
	}
	changeSymbolValInt(symbol: LBSymbol, idx) {
		if (symbol.val[idx]) {
			// Convert the value to number
			symbol.val[idx] = this.convertNumPrecision(symbol.val[idx], this.intervalPrecision);
		}
	}
	onChangeGroupNumberInt(e) {
		this.updateIntervalGroup(e);
	}
	// On change interval max value, check if the values are valid
	// Parametr type: minVal is changed or maxVal is changed
	onChangeMinMaxInt(type: 'min' | 'max') {
		// If min/max doesn't exist, no need to check
		if (!this.symbology.spec.maxVal && this.symbology.spec.maxVal !== 0) {
			return;
		}
		if (!this.symbology.spec.minVal && this.symbology.spec.minVal !== 0) {
			return;
		}
		// Check for min < max
		if (this.symbology.spec.maxVal < this.symbology.spec.minVal) {
			if (type === 'min') { // Update min if minVal is changed to an invalid value
				this.symbology.spec.minVal = this.symbology.spec.maxVal;
			} else { // Update max if maxVal is changed to an invalid value
				this.symbology.spec.maxVal = this.symbology.spec.minVal;
			}
		}
		if (this.intLegendGroupNum) {
			this.updateIntervalGroup(this.intLegendGroupNum);
		}
	}
	// Parameter num: Number of symbol groups
	private updateIntervalGroup(num: number) {
		if (num <= 0) {
			return;
		}
		const precision = this.intervalPrecision || 1;
		// Adjust min/max value to the precision
		const min = this.convertNumPrecision(this.symbology.spec.minVal, precision);
		const max = this.convertNumPrecision(this.symbology.spec.maxVal, precision);
		const interval = (max - min) / num;
		// Minimum interval
		const baseInterval = this.intervalPrecision;
		// Maximum group number basing on the interval precision
		const maxGroupNum = (max - min) / baseInterval;

		// Create a new symbol array
		const symbols = [];
		// if (num > maxGroupNum) {
		// 	for (let i = 0; i < maxGroupNum; i++) {
		// 		let sb = this.symbology.spec.symbols[i];
		// 		if (!sb) { // If symbol doesn't exist, create a new one
		// 			sb = this.symbologysService.getEmptySymbol();
		// 		}
		// 		// Update symbol value
		// 		const fromVal = Number((this.symbology.spec.minVal + baseInterval * i).toFixed(precision));
		// 		const toVal = Number((this.symbology.spec.minVal + baseInterval * (i + 1)).toFixed(precision));
		// 		sb.val = [fromVal, toVal];
		// 		symbols.push(sb);
		// 	}
		// } else {
			for (let i = 0; i < num; i++) {
				let sb = this.symbolInt.symbols[i];
				if (!sb) { // If symbol doesn't exist, create a new one
					sb = this.symbologysService.getEmptySymbol();
				}
				// Update symbol value
				let fromVal = Math.floor((min + interval * i) / baseInterval + 1) * baseInterval;
				fromVal = this.convertNumPrecision(fromVal, precision);
				let toVal = Math.floor((min + interval * (i + 1)) / baseInterval) * baseInterval;
				toVal = this.convertNumPrecision(toVal, precision);
				// Make sure the first symbol is the same as minVal
				if (i === 0) {
					fromVal = min;
				}
				sb.val = [fromVal, toVal];
				symbols.push(sb);
			}
		// }

		// Update symbology's symbols
		this.symbolInt.symbols = symbols;
		// Update legend group num
		this.intLegendGroupNum = symbols.length;
	}
	onChangeIntervalPrecision() {
		// TODO
	}
	trackByFn(idx) {
		return idx;
	}

	cancel() {
		this.dialogRef.close(null);
	}
	apply() {
		// Update symbols basing on valueType
		switch (this.valueType.value) {
			case 'INTERVAL':
				this.symbology.spec.symbols = this.symbolInt.symbols;
				// Check for labels
				this.symbology.spec.symbols.forEach((sb) => {
					if (!sb.lab && sb.val) {
						sb.lab = sb.val[0] + ' - ' + sb.val[1];
					}
				});
				break;
			case 'NOMINAL':
				this.symbology.spec.symbols = this.symbolNom.symbols;
				// Check for labels
				this.symbology.spec.symbols.forEach((sb) => {
					if (!sb.lab && sb.val) {
						sb.lab = sb.val;
					}
				});
				break;
			case 'ORDINAL':
				this.symbology.spec.symbols = this.symbolOrd.symbols;
				// Check for labels
				this.symbology.spec.symbols.forEach((sb) => {
					if (!sb.lab && sb.val) {
						sb.lab = sb.val;
					}
				});
		}
		// Prepare output data
		const data: LbeSymbologyModalOutput = {
			symbology: this.symbology,
			symbolCol: this.selectedSymbCol
		};
		// Close modal
		this.dialogRef.close(data);
	}
	checkType(v) {
		return typeof(v);
	}

	// Change number precision. Note: if X.000, no matter what precision, it'll be converted to X
	private convertNumPrecision(num: number, precision: number): number {
		return Number(num.toFixed( Math.log10(1 / precision)));
	}

	ngOnInit() {
		if (this.data.symbology) { // Editing symbology
			this.action = 'edit';
			// Assign a new object to symbology
			// Keep the original one in data.symbology
			this.symbology = Object.assign({}, this.data.symbology);
			this.columns = this.data.columns || [];
			this.selectedSymbCol = this.columns.find((c) => c.columnId === this.symbology.layerColumnId);
			this.selectedTipCol = this.columns.find((c) => c.name === this.symbology.spec.tipCol);
			this.selectedLabCol = this.columns.find((c) => c.name === this.symbology.spec.labCol);
			switch (this.symbology.spec.symType) {
				case 'INTERVAL':
					this.symbolInt.symbols = this.symbology.spec.symbols;
					break;
				case 'NOMINAL':
					this.symbolNom.symbols = this.symbology.spec.symbols;
					break;
				case 'ORDINAL':
					this.symbolOrd.symbols = this.symbology.spec.symbols;
			}
		} else { // Adding symbology
			this.action = 'add';

			// Empty nominal symbol
			const emptySymbolNom = this.symbologysService.getEmptySymbol();
			this.symbolNom.symbols = [ emptySymbolNom ];
			// Empty interval symbol
			const emptySymbolInt = this.symbologysService.getEmptySymbol();
			emptySymbolInt.val = [];
			this.symbolInt.symbols = [ emptySymbolInt ];
			// Empty symbology
			this.symbology = {
				projectId: null,
				projectName: null,
				layerId: null,
				layerName: null,
				layerColumnId: null,
				layerColumnName: null, // eg. AADT
				layerColumnType: null, // eg. Integer, Text, etc
				id: null,
				spec: {
					name: null,
					labCol: null,
					symCol: null,
					tipCol: null,
					maxVal: null,
					minVal: null,
					reso: null,
					div: null,
					symbols: [],
					symType: null
				}
			};
			//
			this.propsAdd.layers = this.data.layers;
			this.propsAdd.selectedLayer = this.propsAdd.layers[0];
			this.selectLayer();
		}

		this.valueTypes = [
		{
			name: 'Nominal',
			value: 'NOMINAL',
			checkDisable: () => false
		}, {
			name: 'Interval',
			value: 'INTERVAL',
			checkDisable: () => false // this.selectedSymbCol.type !== 'number'
		}, {
			name: 'Ordinal',
			value: 'ORDINAL',
			checkDisable: () => true
		}];
		// Populate valueType according to symbology.
		if (this.symbology.spec.symType) {
			this.valueType = this.valueTypes.find((t) => t.value === this.symbology.spec.symType);
		} else {
			this.valueType = this.valueTypes.find((t) => t.value === this.lbeSettingService.lbeDefaults.misc.symbologyType) || this.valueTypes[0];
		}
		this.changeSymbolType();
	}

}



// WEBPACK FOOTER //
// ./src/app/components/portal/lbe-setting/lbe-symbology-modal/lbe-symbology-modal.component.ts